import { useEffect, useMemo, useState } from 'react';
import defaultDates from '../../../utils/defaultDates';
import getPersonaName from '../../../helpers/getPersonaName';
import useHierarchyData from '../../../hooks/useHierarchyData';
import toMillis from '../../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import MultipleMomentBarChart from '../../Visualisations/moment/MultipleMomentBarChart';
import EventFieldInfoPopover from './EventFieldInfoPopover';
import ChartDataTable from '../ChartDataTable';
import { multipleChoiceTable } from '../../../helpers/dataHelpers/chartDataTable';
import { buildTemplateEventResult } from './templateEventRecords';
import useChartColors from '../../../hooks/useChartColors';

// AI-CONTEXT: See docs/features/api-data-fetching.md

const ParticipantsData = ({ field, color, startDate, endDate, activityId, templateActivityData }) => {
  const isTemplate = !!templateActivityData;
  const [chartData, setChartData] = useState([]);

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), []);
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw]);
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw]);
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs]);
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs]);

  const { palette: PALETTE } = useChartColors();

  const live = useHierarchyData({
    syncType: 'Event',
    id: isTemplate ? '' : field.ID,
    startDate: startMs,
    endDate: endMs,
  });

  const template = useMemo(
    () => buildTemplateEventResult(templateActivityData, field.ID),
    [templateActivityData, field.ID]
  );

  const records = isTemplate ? (template?.records || []) : live.records;
  const projectHierarchy = isTemplate ? (template?.hierarchy || {}) : live.hierarchy;
  const loading = isTemplate ? false : live.loading;

  // Resolve persona names (async) after records arrive
  useEffect(() => {
    if (loading || !records.length) {
      setChartData([]);
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const countsByValue = new Map();
      for (const r of records) {
        const key = r.Value ?? '__empty__';
        countsByValue.set(key, (countsByValue.get(key) || 0) + 1);
      }

      const entries = [...countsByValue.entries()];
      entries.sort((a, b) => b[1] - a[1]);

      const responses = await Promise.all(
        entries.map(async ([value, count], idx) => {
          let name;
          try { name = await getPersonaName(value); } catch { name = String(value); }
          return { name, count, color: PALETTE[idx % PALETTE.length] };
        })
      );

      if (!cancelled) {
        setChartData([{ moment: 'Totaal', responses, others: [] }]);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [records, loading, PALETTE]);

  return (
    <>
      <div className='home-kpi-question-title-container'>
        {field?.Title && <p className='event-field-title'>{field.Title}</p>}
        <EventFieldInfoPopover field={field} activityId={activityId} totalResponses={records.length} projectHierarchy={projectHierarchy} />
      </div>

      <MultipleMomentBarChart
        data={chartData}
        projectAverages={{}}
        totalResponses={records.length}
        projectHierarchy={projectHierarchy}
        color={color}
        source="event"
      />
      <ChartDataTable table={multipleChoiceTable(chartData)} />
    </>
  );
};

export default ParticipantsData;
